import PropTypes from 'prop-types';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText, Typography, Tooltip } from '@mui/material';
import NavItem from '../NavItem';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';

const NavCollapse = ({ menu, level, open }) => {
    const theme = useTheme();
    const customization = useSelector((state) => state.customization);

    const [expanded, setExpanded] = useState(false);
    const [selected, setSelected] = useState(null);

    const handleClick = () => {
        if (!open) return; // don’t expand when collapsed
        setExpanded(!expanded);
        setSelected(!selected ? menu.id : null);
    };

    const menus = menu.children?.map((item) => {
        switch (item.type) {
            case 'collapse':
                return <NavCollapse key={item.id} menu={item} level={level + 1} open={open} />;
            case 'item':
                return <NavItem key={item.id} item={item} level={level + 1} open={open} />;
            default:
                return (
                    <Typography key={item.id} variant="h6" color="error" align="center">
                        Menu Items Error
                    </Typography>
                );
        }
    });

    const Icon = menu.icon;
    const menuIcon = Icon ? (
        <Icon sx={{ mt: 'auto', mb: 'auto', fontSize: 18 }} />
    ) : (
        <FiberManualRecordIcon
            sx={{
                width: selected === menu.id ? 8 : 6,
                height: selected === menu.id ? 8 : 6
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );

    const button = (
        <ListItemButton
            sx={{
                borderRadius: `${customization.borderRadius}px`,
                alignItems: 'flex-start',
                backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
                py: 1,
                pl: open ? `${level * 24}px` : 1,
                justifyContent: open ? 'initial' : 'center'
            }}
            onClick={handleClick}
        >
            <ListItemIcon
                sx={{
                    my: 'auto',
                    minWidth: open ? (!menu.icon ? 18 : 36) : 0,
                    mr: open ? 1.5 : 0,
                    justifyContent: 'center'
                }}
            >
                {menuIcon}
            </ListItemIcon>

            <ListItemText
                primary={
                    <Typography variant="h6" color="inherit" sx={{ my: 'auto' }}>
                        {menu.title}
                    </Typography>
                }
                secondary={
                    menu.caption && open && (
                        <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block" gutterBottom>
                            {menu.caption}
                        </Typography>
                    )
                }
                sx={{ opacity: open ? 1 : 0 }}
                primaryTypographyProps={{ noWrap: true }}
            />

            {/* Hide chevrons when collapsed */}
            {open && (expanded ? <KeyboardArrowUp sx={{ fontSize: 16, my: 'auto' }} /> : <KeyboardArrowDown sx={{ fontSize: 16, my: 'auto' }} />)}
        </ListItemButton>
    );

    return (
        <>
            {open ? (
                button
            ) : (
                <Tooltip title={menu.title} placement="right">
                    {button}
                </Tooltip>
            )}

            <Collapse in={open && expanded} timeout="auto" unmountOnExit>
                <List
                    component="div"
                    disablePadding
                    sx={{
                        position: 'relative',
                        '&:after': {
                            content: "''",
                            position: 'absolute',
                            left: '32px',
                            top: 0,
                            height: '100%',
                            width: '1px',
                            opacity: 1,
                            background: theme.palette.primary.light
                        }
                    }}
                >
                    {menus}
                </List>
            </Collapse>
        </>
    );
};

NavCollapse.propTypes = {
    menu: PropTypes.object,
    level: PropTypes.number,
    open: PropTypes.bool
};

export default NavCollapse;